<template>    
    <div class="search-container" ref="searchContainer">
        <div class="search-bar-container">
            <div class="search-bar">
                <span class="input-group-text border-0 bg-transparent" id="search-addon">
                    <i class="fas fa-search"></i>
                </span>
                <input
                    class="search-input border-0 text-xs"
                    type="text"
                    placeholder="Search..."
                    v-model="global_search_query"
                    @input="handle_global_search"
                    @focus="handle_search_focus"
                />
                <button
                    v-if="global_search_query"
                    class="clear-search-btn"
                    @click="clear_search"
                    type="button"
                    aria-label="Clear search"
                >
                    <i class="fas fa-times"></i>
                </button>
            </div>
            <transition name="fade" appear>
                <div v-if="show_results && filtered_search_results"
                    class="search-results-dropdown"
                >
                    <div v-if="has_results && filtered_search_results" class="results-container p-3">
                        <div
                            v-for="(result, category) in filtered_search_results"
                            :key="category"
                            class="category-section"
                        >
                            <!-- Users Category -->
                            <div v-if="category === 'users' && is_category_allowed('users') && filtered_search_results[category] !== 'No results found'">
                                <h6 class="category-title">
                                    <i class="fas fa-users"></i>
                                    Users
                                </h6>

                                <div v-for="role in ['Customer', 'ClientAdmin', 'Client', 'SystemAdmin']" :key="role">
                                    <div v-if="is_user_role_viewable(role) && get_filtered_users_by_role(role).length > 0">
                                        <div class="role-header">{{ role }}s</div>
                                        
                                        <div
                                            v-for="user in get_filtered_users_by_role(role)"
                                            :key="user.id"
                                            class="result-card user-card"
                                            @click="go_to_user(user.id, user.user_role)"
                                        >
                                            <div class="card-header">
                                                <div class="user-avatar">
                                                    <i class="fas fa-user"></i>
                                                </div>
                                                <div class="user-info">
                                                    <div class="user-name">{{ user.first_name }} {{ user.last_name }}</div>
                                                    <div class="user-role-badge">{{ user.user_role }}</div>
                                                </div>
                                            </div>
                                            
                                            <div class="card-body">
                                                <div class="contact-info">
                                                    <div class="info-item">
                                                        <i class="fas fa-phone"></i>
                                                        <span>{{ user.phone_number }}</span>
                                                    </div>
                                                    <div class="info-item">
                                                        <i class="fas fa-id-card"></i>
                                                        <span>{{ user.id_number }}</span>
                                                    </div>
                                                    <div class="info-item">
                                                        <i class="fas fa-envelope"></i>
                                                        <span>{{ user.email }}</span>
                                                    </div>
                                                </div>
                                                <div v-if="user.workflows?.length && can_view_workflows" class="workflows-section">
                                                    <div class="workflows-header">
                                                        <i class="fas fa-project-diagram"></i>
                                                        <span>Workflows</span>
                                                    </div>
                                                    <div class="workflows-list">
                                                        <div
                                                            v-for="wf in user.workflows"
                                                            :key="wf.id"
                                                            class="workflow-item"
                                                            @click.stop="view_customer_applications(user.id, wf.id)"
                                                        >
                                                            <span class="workflow-name">{{ wf.workflow_name }}</span>
                                                            <i class="fas fa-chevron-right"></i>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Workflows Category -->
                            <div v-if="category === 'workflows' && is_category_allowed('workflows') && filtered_search_results[category] !== 'No results found'">
                                <h6 class="category-title">
                                    <i class="fas fa-project-diagram"></i>
                                    Workflows
                                </h6>
                                <div
                                    v-for="workflow in filtered_search_results.workflows" 
                                    :key="'workflow-'+workflow.id"
                                    class="result-card workflow-category-card"
                                >
                                    <div class="card-header">
                                        <div class="workflow-category-icon">
                                            <i class="fas fa-cogs"></i>
                                        </div>
                                        <div class="workflow-category-info">
                                            <div class="workflow-category-name">{{ workflow.workflow_name }}</div>
                                            <div class="workflow-category-description">{{ workflow.workflow_description }}</div>
                                        </div>
                                    </div>

                                    <div class="card-body mt-0" v-if="user_current_role !== 'Customer'">
                                        <div class="workflows-header">
                                            <span>Action</span>
                                        </div>
                                        <div class="workflow-actions">
                                            <div
                                                class="workflow-item"
                                                @click.stop="view_customers(workflow.id, global_search_results.current_user_role)"
                                            >
                                                <span class="workflow-name">View Customers</span>
                                                <i class="fas fa-chevron-right"></i>
                                            </div>
                                            <div
                                                class="workflow-item"
                                                @click.stop="edit_workflow(workflow.id)"
                                            >
                                                <span class="workflow-name">Edit</span>
                                                <i class="fas fa-chevron-right"></i>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Services Category -->
                            <div v-if="category === 'services' && is_category_allowed('services') && filtered_search_results[category] !== 'No results found'">
                                <h6 class="category-title">
                                    <i class="fas fa-concierge-bell"></i>
                                    Services
                                </h6>
                                <div
                                    v-for="service in filtered_search_results.services" 
                                    :key="'service-'+service.id"
                                    class="result-card service-card"
                                    @click="go_to_service(service.id, service.service_type.service_type)"
                                >
                                    <div class="card-header">
                                        <div class="service-icon">
                                            <i class="fas fa-wrench"></i>
                                        </div>
                                        <div class="service-info">
                                            <div class="service-name">{{ service.service_name }}</div>
                                            <div class="service-type">{{ service.service_type.service_type }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Projects Category -->
                            <div v-if="category === 'projects' && is_category_allowed('projects') && filtered_search_results[category] !== 'No results found'">
                                <h6 class="category-title">
                                    <i class="fas fa-folder-open"></i>
                                    Projects
                                </h6>
                                <div
                                    v-for="project in filtered_search_results.projects" 
                                    :key="'project-'+project.id"
                                    class="result-card project-card"
                                    @click="go_to_project(project.id)"
                                >
                                    <div class="card-header">
                                        <div class="project-icon">
                                            <i class="fas fa-tasks"></i>
                                        </div>
                                        <div class="project-info">
                                            <div class="project-name">{{ project.project_name }}</div>
                                            <div class="project-description">{{ project.project_description }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Assets Category -->
                            <div v-if="category === 'assets' && is_category_allowed('assets') && filtered_search_results[category] !== 'No results found' && user_current_role !== 'SystemAdmin'">
                                <h6 class="category-title">
                                    <i class="fas fa-database"></i>
                                    Assets
                                </h6>
                                <div
                                    v-for="asset in filtered_search_results.assets" 
                                    :key="'asset-'+asset.id"
                                    class="result-card asset-card"
                                    @click="go_to_asset(asset.id)"
                                >
                                    <div class="card-header">
                                        <div class="asset-icon">
                                            <i class="fas fa-cube"></i>
                                        </div>
                                        <div class="asset-info">
                                            <div class="asset-name">{{ asset.asset_name }}</div>
                                            <div class="asset-description">{{ asset.asset_description }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Templates Category -->
                            <div v-if="category === 'templates' && is_category_allowed('templates') && filtered_search_results[category] !== 'No results found'">
                                <h6 class="category-title">
                                    <i class="fas fa-file-alt"></i>
                                    Templates
                                </h6>
                                <div
                                    v-for="template in filtered_search_results.templates" 
                                    :key="'template-'+template.id"
                                    class="result-card template-card"
                                    @click="go_to_template(template.id)"
                                >
                                    <div class="card-header">
                                        <div class="template-icon">
                                            <i class="fas fa-file-code"></i>
                                        </div>
                                        <div class="template-info">
                                            <div class="template-name">{{ template.template_name }}</div>
                                            <div class="template-description">{{ template.description }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- Questions Category -->
                            <div v-if="category === 'questions' && is_category_allowed('questions') && filtered_search_results[category] !== 'No results found'">
                                <h6 class="category-title">
                                    <i class="fas fa-question-circle"></i>
                                    Questions
                                </h6>
                                <div
                                    v-for="question in filtered_search_results.questions" 
                                    :key="'question-'+question.id"
                                    class="result-card question-card"
                                    @click="go_to_question(question.id)"
                                >
                                    <div class="card-header">
                                        <div class="question-icon">
                                            <i class="fas fa-question"></i>
                                        </div>
                                        <div class="question-info">
                                            <div class="question-text">{{ question.question }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-else class="no-results">
                        <i class="fas fa-search"></i>
                        <div>No results found for "{{ global_search_query }}"</div>
                    </div>
                </div>
            </transition>
        </div>
    </div>
</template>
